import React from 'react';
import { PracticeType } from '../../providers/practices/PracticesProvider';
import { formatPhone } from '../../utils/contact';
import TeamMembers from './TeamMembers';

interface PracticeSectionProps {
    practice: PracticeType;
    isExpanded: boolean;
    onToggle: () => void;
    onEdit: () => void;
}

const PracticeSection: React.FC<PracticeSectionProps> = ({
    practice,
    isExpanded,
    onToggle,
    onEdit
}) => {
    const doctorName =
        `Dr. ${practice?.doctorFirstName ?? ''} ${practice?.doctorLastName ?? ''}`.trim();

    return (
        <div
            className={`rounded-xl border transition-all duration-300 ${
                isExpanded
                    ? 'border-gray-300 shadow-md bg-white'
                    : 'border-gray-200 shadow-sm bg-white hover:border-gray-300'
            }`}
        >
            {/* Collapsed header */}
            <div
                role="button"
                tabIndex={0}
                onClick={onToggle}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onToggle();
                    }
                }}
                className="w-full flex items-center justify-between px-5 py-4 group cursor-pointer"
            >
                <div className="flex items-center gap-4">
                    <div
                        className={`flex items-center justify-center w-10 h-10 rounded-full transition-colors duration-200 ${
                            isExpanded ? 'bg-gray-800' : 'bg-gray-100 group-hover:bg-gray-200'
                        }`}
                    >
                        <svg
                            className={`w-5 h-5 transition-all duration-200 ${
                                isExpanded ? 'text-white' : 'text-gray-600'
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="1.5"
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                        </svg>
                    </div>
                    <div className="flex flex-col items-start">
                        <span className="font-semibold text-gray-900 text-base">
                            {practice?.name ?? 'Unnamed Practice'}
                        </span>
                        {doctorName !== 'Dr.' && (
                            <span className="text-sm text-gray-500">{doctorName}</span>
                        )}
                    </div>
                </div>
                <svg
                    className={`w-5 h-5 text-gray-400 transition-transform duration-300 ease-out ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </div>

            {/* Expanded content */}
            <div
                className={`grid transition-all duration-300 ease-out ${
                    isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}
            >
                <div className="overflow-hidden">
                    <div className="px-5 pb-5 flex flex-col gap-5">
                        <div className="h-px bg-gray-200" />

                        {/* Practice Details sub-section */}
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-semibold text-gray-500">
                                    Practice Details
                                </span>
                                <button
                                    onClick={onEdit}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    <svg
                                        className="w-3.5 h-3.5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                        />
                                    </svg>
                                    Edit
                                </button>
                            </div>

                            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-xs font-medium text-gray-400">Name</span>
                                    <span className="text-sm text-gray-800">
                                        {practice?.name ?? ''}
                                    </span>
                                </div>

                                <div className="flex flex-col gap-0.5">
                                    <span className="text-xs font-medium text-gray-400">
                                        Doctor
                                    </span>
                                    <span className="text-sm text-gray-800">
                                        {doctorName !== 'Dr.' ? doctorName : '-'}
                                    </span>
                                </div>

                                <div className="flex flex-col gap-0.5">
                                    <span className="text-xs font-medium text-gray-400">Phone</span>
                                    <span className="text-sm text-gray-800">
                                        {practice?.phone ? formatPhone(practice.phone) : '-'}
                                    </span>
                                </div>

                                <div className="flex flex-col gap-0.5">
                                    <span className="text-xs font-medium text-gray-400">
                                        Address
                                    </span>
                                    <span className="text-sm text-gray-800">
                                        {practice?.address ?? '-'}
                                        {(practice?.city || practice?.state || practice?.zip) && (
                                            <br />
                                        )}
                                        {[
                                            practice?.city ?? '',
                                            practice?.state ?? '',
                                            practice?.zip ?? ''
                                        ]
                                            .filter(Boolean)
                                            .join(', ')}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="h-px bg-gray-200" />

                        {/* Team Members sub-section */}
                        <TeamMembers practiceId={practice?.id ?? ''} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PracticeSection;
